import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { publicApi as api, formatApiError } from "@/lib/api";
import { IMAGES, CONTACT_PEOPLE, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";

export default function Contact() {
  const { t, lang } = useLanguage();
  usePageMeta({ ...PAGE_META[lang].contact, lang, image: IMAGES.legiaPark });
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error(c.form.errRequired);
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success(c.form.ok);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-y5-ink border border-y5-line px-4 py-3.5 text-white placeholder:text-white/30 focus:border-y5-yellow focus:outline-none transition-colors";

  return (
    <div className="pt-32 pb-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{c.kicker}</span>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight uppercase mt-4 mb-8 leading-[0.9]">
            <RevealLines lines={c.title} />
          </h1>
          <p className="text-y5-muted text-lg leading-relaxed mb-12 max-w-md">{c.subtitle}</p>
          <div className="space-y-4">
            {CONTACT_PEOPLE.map((p) => (
              <div key={p.email} data-testid={`contact-person-${p.email}`} className="border border-y5-line bg-y5-surface p-5 hover:border-y5-yellow transition-colors flex items-start gap-4">
                {p.photo && (
                  <img
                    src={p.photo}
                    alt={p.name}
                    loading="lazy"
                    className="w-20 h-20 shrink-0 object-cover rounded-full ring-2 ring-y5-yellow"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white font-display text-xl">{p.name}</p>
                  <p className="text-y5-yellow text-xs uppercase tracking-widest mt-1 mb-3">{c.roles[p.roleKey]}</p>
                  <div className="space-y-1.5 text-sm">
                    <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-white/80 hover:text-y5-yellow transition-colors break-all"><Mail size={15} className="text-y5-yellow shrink-0" /> {p.email}</a>
                    {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/80 hover:text-y5-yellow transition-colors"><Phone size={15} className="text-y5-yellow shrink-0" /> {p.phone}</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 border border-y5-line overflow-hidden">
            <div className="relative h-44">
              <img src={IMAGES.legiaPark} alt="LégiaPark" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-y5-ink/20" />
            </div>
            <div className="p-5 flex items-start gap-3">
              <MapPin size={18} className="text-y5-yellow mt-0.5 shrink-0" />
              <div>
                <p className="text-white font-medium">{c.office}</p>
                <p className="text-y5-muted text-sm mt-1">{c.address}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-y5-surface border border-y5-line p-8">
            {done ? (
              <div data-testid="contact-success" className="text-center py-16">
                <ArrowUpRight className="mx-auto text-y5-yellow mb-4" size={40} />
                <h3 className="font-display text-3xl mb-3">{c.form.successTitle}</h3>
                <p className="text-y5-muted">{c.form.successText}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input name="name" data-testid="contact-name" value={form.name} onChange={onChange} placeholder={c.form.name} className={inputCls} />
                  <input name="email" data-testid="contact-email" type="email" value={form.email} onChange={onChange} placeholder={c.form.email} className={inputCls} />
                </div>
                <input name="company" data-testid="contact-company" value={form.company} onChange={onChange} placeholder={c.form.company} className={inputCls} />
                <textarea name="message" data-testid="contact-message" value={form.message} onChange={onChange} placeholder={c.form.message} rows={6} className={inputCls} />
                <button type="submit" data-testid="contact-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-y5-yellow text-y5-ink font-semibold py-4 hover:scale-[1.01] transition-transform disabled:opacity-60">
                  {loading ? <><Loader2 className="animate-spin" size={18} /> {c.form.sending}</> : c.form.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
